import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextField } from '@wix/wix-ui-tpa/cssVars';
import { useConfig } from '../../../../contexts/config-context';
import { useSubmitSuccessEvent, useFocusFieldEvent } from '../../../../contexts/events-context';
import { useFocus } from '../../../../hooks/use-focus';
import { parseCountryCodeFromPhoneNumber } from '../phone-input-utils';
import { filterCountries, searchCountries, useCountries } from '../use-countries';
import { DrawerWithSearch } from './drawer-with-search';
import { DropdownWithSearch } from './dropdown-with-search';
import { classes, st } from './phone-input-wut.st.css';
import { classes as countryOptionClasses } from './country-option.st.css';
import { Flag } from '../flag';
export const PhoneInputWUT = _ref => {
  let {
    fieldId,
    inputId,
    value,
    allowedCountryCodes,
    defaultCountry,
    showFlag,
    placeholder,
    disabled,
    required,
    hasError,
    ariaDescribedBy,
    ariaInvalid,
    className,
    theme,
    readOnly,
    onChange,
    onFocus,
    onBlur,
    ariaLabel
  } = _ref;
  const {
    isMobile,
    isRTL: rtl
  } = useConfig();
  const phoneInputRef = useRef(null);
  const [searchPhrase, setSearchPhrase] = useState('');
  const countryCodeFromPhoneNumber = useMemo(() => parseCountryCodeFromPhoneNumber(value), [value]);
  const [selectedCountryCode, setSelectedCountryCode] = useState(defaultCountry ?? countryCodeFromPhoneNumber);
  const allCountries = useCountries();
  const allowedCountries = useMemo(() => filterCountries(allCountries, allowedCountryCodes), [allCountries, allowedCountryCodes]);
  const countries = useMemo(() => searchCountries(allowedCountries, searchPhrase), [allowedCountries, searchPhrase]);
  const isValidCountryCode = useMemo(() => allowedCountries.find(country => country.shortCountryCode === selectedCountryCode), [allowedCountries, selectedCountryCode]);
  const validCountryCode = isValidCountryCode ? selectedCountryCode : undefined;
  useSubmitSuccessEvent(() => {
    setSelectedCountryCode(defaultCountry);
  });
  useEffect(() => {
    setSelectedCountryCode(defaultCountry ?? countryCodeFromPhoneNumber);
  }, [countryCodeFromPhoneNumber, defaultCountry]);
  const handleKeydown = useCallback(event => {
    var _phoneInputRef$curren;
    switch (event.key) {
      case 'Tab':
        (_phoneInputRef$curren = phoneInputRef.current) == null || _phoneInputRef$curren.focus();
        break;
    }
  }, []);
  const handleSelect = useCallback(countryCode => {
    var _phoneInputRef$curren2;
    setSelectedCountryCode(countryCode);
    onChange(value, countryCode);
    (_phoneInputRef$curren2 = phoneInputRef.current) == null || _phoneInputRef$curren2.focus();
  }, [value, onChange]);
  const handleChange = useCallback(event => {
    const countryCodeByPhoneNumber = parseCountryCodeFromPhoneNumber(event.target.value);
    if (countryCodeByPhoneNumber) {
      setSelectedCountryCode(countryCodeByPhoneNumber);
      onChange(event.target.value, countryCodeByPhoneNumber);
      return;
    }
    onChange(event.target.value, selectedCountryCode);
  }, [onChange, selectedCountryCode]);
  const {
    containerRef,
    handleBlur,
    handleFocus
  } = useFocus({
    onBlur,
    onFocus
  });
  useFocusFieldEvent(() => {
    var _phoneInputRef$curren3;
    (_phoneInputRef$curren3 = phoneInputRef.current) == null || _phoneInputRef$curren3.focus({
      preventScroll: true
    });
  }, fieldId);
  const renderPrefix = () => {
    if (allowedCountries.length === 1) {
      return showFlag ? /*#__PURE__*/React.createElement("div", {
        className: countryOptionClasses.flag
      }, /*#__PURE__*/React.createElement(Flag, {
        longCountryCode: countries[0].longCountryCode
      })) : null;
    }
    return isMobile ? /*#__PURE__*/React.createElement(DrawerWithSearch, {
      readOnly: readOnly,
      className: className,
      theme: theme,
      countries: countries,
      countryCode: validCountryCode,
      onSelect: handleSelect,
      searchPhrase: searchPhrase,
      onSearch: setSearchPhrase,
      onKeyDown: handleKeydown,
      ariaLabel: ariaLabel
    }) : /*#__PURE__*/React.createElement(DropdownWithSearch, {
      readOnly: readOnly,
      disabled: disabled,
      className: className,
      theme: theme,
      countries: countries,
      countryCode: validCountryCode,
      onSelect: handleSelect,
      searchPhrase: searchPhrase,
      onSearch: setSearchPhrase,
      onKeyDown: handleKeydown,
      ariaLabel: ariaLabel
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    onBlur: handleBlur,
    onFocus: handleFocus
  }, /*#__PURE__*/React.createElement(TextField, {
    ref: phoneInputRef,
    id: inputId,
    type: "phone",
    inputMode: "tel",
    error: hasError,
    newErrorMessage: true,
    value: typeof value === 'string' ? value : '',
    className: st(classes.input, {
      rtl
    }, className),
    required: required,
    disabled: disabled,
    placeholder: placeholder,
    theme: theme,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    "aria-label": ariaLabel,
    onChange: handleChange,
    prefix: renderPrefix(),
    readOnlyCustom: readOnly
  }));
};
//# sourceMappingURL=phone-input-wut.js.map